import { appBannerChromeMarkup, inlineStyle } from './appBannerChrome';
import { appDesignMarkup } from './appDesignMarkup';
import { MOBILE_DESIGN_WIDTH, MOBILE_VIEWPORT_HEIGHT } from './previewFit';

// A preview is rendered by the SDK's own render path rather than by an
// approximation of it here: `releva-preview.min.js` is a second webpack entry of
// magellan-sdk-js, built from the same modules as the bundle a shop loads, and
// exposing `renderBanner`/`renderRecommender` and nothing else — no api client,
// no cookies, no storage, no tracking. Unpinned, like the shop's own tag, so the
// preview and the shop stay in lockstep instead of drifting a release apart.
export const PREVIEW_BUNDLE_URL = 'https://releva.ai/sdk/v0/js/releva-preview.min.js';

// The frame is sandboxed without `allow-same-origin`, so its document is an
// opaque origin the admin cannot read: everything the parent needs to know comes
// back as a message of this type.
export const PREVIEW_MESSAGE_TYPE = 'RLV_PREVIEW';

export const PREVIEW_CONTAINER_ID = 'rlv-preview-root';

// The global a banner's own scripts expect. On a shop they are evaluated with
// the SDK already on the page, so markup authored in the editor calls
// `Releva.requestSubscribe(...)`, `Releva.trackEvent(...)` or
// `Releva.getCookie(...)` freely — a subscription pop-up is mostly that. The
// preview frame loads `releva-preview.min.js`, which publishes `RelevaPreview`
// and nothing else, so every one of those calls was a `ReferenceError`: the
// SDK runs a banner's scripts (`evalChildScripts`) inside the `renderBanner`
// call itself, so the throw came back as "Unable to show preview: Releva is
// not defined" and the merchant lost the whole preview over markup that is
// perfectly fine.
//
// So the frame gets an inert stand-in. Not the real SDK, deliberately: the
// preview bundle exists precisely so that nothing here can emit an
// impression, a push, a subscription or a fingerprint attributed to whoever
// is editing the banner. The preview is inert anyway — it is drawn with
// `pointer-events: none` — so nothing the stub swallows was going to happen
// on this screen.
//
// The exports split in two. Most of them report through callbacks or a
// promise on a shop (`requestSubscribe`, `checkSubscribed`, `trackEvent`,
// `push`) and their answer here is a resolved promise: "nothing happened",
// and a `.then`-able for the ones a banner's script chains off. The rest
// answer immediately on a shop. A promise is not an empty answer for those,
// it is the wrong type: a subscription pop-up does
// `"releva-form-" + Releva.uuidv4()` and queries the document for that id, so
// a promise there became the id `releva-form-[object Promise]` and a
// `querySelector` SyntaxError five seconds later. Those get an answer of the
// right shape that still does nothing — a real uuid, no cookie, no url
// parameter.
//
// IMPORTANT: serialised with `Function.prototype.toString` and injected into
// the frame, so it must stay self-contained — no imports, no module
// constants, nothing from its closure. Everything it needs is `global`.
export function installPreviewSdkStub(global) {
  if (global.Releva) {
    return;
  }

  var inert = function () {
    return global.Promise.resolve();
  };

  var immediate = {
    uuidv4: function () {
      // Unique per call: banners use these as DOM ids, and two blocks sharing
      // one would have the second script find the first one's markup.
      if (global.crypto && typeof global.crypto.randomUUID === 'function') {
        return global.crypto.randomUUID();
      }

      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (placeholder) {
        var digit = global.Math.floor(global.Math.random() * 16);
        return (placeholder === 'x' ? digit : (digit & 0x3) | 0x8).toString(16);
      });
    },
    getCookie: function () {
      return null;
    },
    getUrlSearchParameterByName: function () {
      return null;
    },
    hashCode: function () {
      // A constant collides every input into one bucket — the same hazard
      // `uuidv4` had — so this still hashes, just not into anything the real
      // SDK's bucketing would reproduce.
      var str = global.String(arguments[0]);
      var hash = 0;
      for (var i = 0; i < str.length; i += 1) {
        hash = (hash * 31 + str.charCodeAt(i)) | 0;
      }
      return hash;
    },
    cookiesUsed: function () {
      return [];
    },
    dumpState: function () {
      return { v: 'v1', cookies: [] };
    },
    version: function () {
      return '0.0.0-preview';
    },
    // Not a function in the SDK at all, but a colour constant — so it is
    // handed back as one rather than wrapped in anything.
    EDIT_CHROME_COLOR: '#904DFF',
  };

  global.Releva = new global.Proxy(
    {},
    {
      get: function (target, name) {
        // Whatever an object already answers for — `toString`, `valueOf`,
        // `constructor` — keeps answering for itself, or `String(Releva)`
        // would call the stub and then fail to turn a promise into a string.
        if (name in target) {
          return global.Reflect.get(target, name);
        }

        // Never a thenable: a stubbed `then` would be handed the resolve
        // callback of anything that awaited the namespace and never call it,
        // hanging the script instead of doing nothing.
        if (name === 'then' || typeof name === 'symbol') {
          return undefined;
        }

        if (name in immediate) {
          return immediate[name];
        }

        return inert;
      },
    }
  );
}

// Runs inside the preview frame: hands the block to the SDK and reports back
// what the parent can no longer see for itself — the rendered height, whether
// the SDK will only show this banner at a mobile width, and any failure.
//
// IMPORTANT: serialised with `Function.prototype.toString` and injected into the
// frame, so it must stay self-contained — no imports, no module constants,
// nothing from its closure. Everything it needs arrives in `options`.
export function bootstrapSdkPreview(options) {
  var post = function (message) {
    message.type = options.messageType;
    window.parent.postMessage(message, options.parentOrigin);
  };

  // A <script src> that 404s or is blocked by an extension leaves the global
  // unset, and the parser has already given up on it by the time this inline
  // script runs — so an absent global is the signal, whichever way it failed.
  if (!window.RelevaPreview) {
    post({ bundleFailed: true });
    return;
  }

  var container = document.getElementById(options.containerId);
  var result;
  var scriptError = null;

  try {
    result = window.RelevaPreview[options.method](options.payload, container);
  } catch (e) {
    var reason = (e && e.message) || String(e);

    // The SDK appends the block's markup and only then runs the scripts that
    // markup carries, so a throw from one of those scripts leaves the block
    // itself on screen. Replacing a rendered preview with a line of error text
    // because a slider did not boot costs the merchant more than it tells
    // them — the block is what they came to look at. Only a failure that left
    // nothing behind is fatal; anything else is reported beside the preview.
    if (!container || container.childElementCount === 0) {
      post({ error: reason });
      return;
    }

    scriptError = reason;
  }

  var mobileOnly = !!(result && result.mobileOnly);

  var measure = function () {
    var style = window.getComputedStyle(document.body);

    post({
      height: document.body.scrollHeight + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0),
      mobileOnly: mobileOnly,
      scriptError: scriptError,
    });
  };

  measure();

  // The same thing one tick later: a banner's script that throws from a timer,
  // an [onload] attribute the parser fired itself, or a promise's callback
  // never reaches the try above, and used to be an "Uncaught ReferenceError"
  // in a console nobody has open and a silently half-built preview. Not
  // captured, so a merchant's broken image url — which fires an error event at
  // the element, not at the window — stays the non-event it is.
  window.addEventListener('error', function (event) {
    scriptError = (event.error && event.error.message) || event.message;
    measure();
  });

  // A wizard layout ships its block hidden and relies on an [onload] handler —
  // booting a slider — to reveal it. `evalChildScripts` runs that handler, but
  // when the slider's library fails to load the handler throws and nothing ever
  // un-hides the block: the merchant gets an empty preview and no reason for it.
  // So if anything is still hidden a beat later, show it. A block whose slider
  // did not start looks wrong; a preview that renders nothing reads as "my block
  // is broken", which is worse and is the bug this preview existed to fix.
  window.setTimeout(function () {
    var hidden = document.querySelectorAll('.rlv-recommender');
    var revealed = false;

    for (var i = 0; i < hidden.length; i += 1) {
      if (window.getComputedStyle(hidden[i]).display === 'none') {
        hidden[i].style.setProperty('display', 'block', 'important');
        revealed = true;
      }
    }

    if (revealed) {
      measure();
    }
  }, 2500);

  // A layout reveals itself asynchronously — the slider's onInit is what flips
  // the block from `display: none` — so measuring once is not enough. The
  // observer belongs to this document and is discarded with it.
  if (typeof window.ResizeObserver === 'function') {
    new window.ResizeObserver(measure).observe(document.body);
  }
}

// JSON is valid JavaScript, but a `</script` inside a string would end the
// script element — and a banner's html can easily contain one. Escaping every
// `<` keeps the payload inside its own script, `<!--` included.
const inlineJson = (value) => JSON.stringify(value).replace(/</g, '\\u003c');

// Relative URLs in a banner's markup would otherwise resolve against the admin's
// own origin, so the document points them at the site the content belongs to.
// `<base>` applies in a sandboxed srcDoc frame just as it does anywhere else.
const baseTag = (baseUrl) => {
  if (!baseUrl) {
    return '';
  }

  try {
    return `<base href="${new URL(baseUrl).href}">`;
  } catch {
    return '';
  }
};

// What the app SDKs show for a banner they will not draw: nothing at all. Said
// in the frame rather than left as an empty card, because an empty card reads
// as a banner that failed to render.
const appNoteMarkup = (note) =>
  `<div style="${inlineStyle({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'center',
    padding: '24px',
    'box-sizing': 'border-box',
    font: '14px/1.5 system-ui,sans-serif',
    color: '#555',
    'text-align': 'center',
    'background-color': '#fff',
  })}">${note}</div>`;

const APP_NOT_DRAWN_NOTE =
  'The apps do not show this banner. They build it from the design the editor saves, and this one has none — ' +
  'only HTML, which no app SDK renders.';

// No banner chosen yet, or the banner list has not loaded.
const APP_NO_BANNER_NOTE = 'Choose a banner to see how the apps draw it.';

// The same frame, showing what the NATIVE app SDKs draw instead of what the
// web SDK draws. The preview bundle is deliberately absent: it would build the
// web SDK's own card and close button, which is the very difference this
// render exists to show.
//
// Neither half of the render is the banner's html. The chrome is markup
// resolved from the block's keys the way the apps resolve them
// (`appBannerChrome`); the content inside it is built from the Unlayer design
// JSON (`appDesignMarkup`), which is the only thing the native renderers read.
// A banner with no design is one `shouldDisplay` refuses outright
// (sdk-react-native's `BannerDisplayWidget`), so it gets a note and no card. There is no inert SDK stand-in any more either: nothing in this
// document can carry a script now that the html never reaches it.
export const buildAppPreviewDocument = ({ payload, baseUrl }) => {
  const head = `<!doctype html><html><head><meta charset="utf-8">${baseTag(baseUrl)}<style>html,body{margin:0;padding:0}</style></head>`;

  if (!payload.design) {
    const note = payload.html === undefined ? APP_NO_BANNER_NOTE : APP_NOT_DRAWN_NOTE;
    return `${head}<body>${appNoteMarkup(note)}</body></html>`;
  }

  return (
    `${head}<body>` +
    appBannerChromeMarkup({
      ...payload,
      html: appDesignMarkup(payload.design, { displayType: payload.displayType }),
      // The design's own card fallbacks — `popupWidth`, `borderRadius`,
      // `popupBackgroundColor`, `backgroundImage`, `popupOverlay_*`.
      designValues: payload.design.body?.values,
      // An app render is always a phone, and the frame is sized to exactly
      // this viewport — the card's caps and any percentage offset are measured
      // against it.
      viewport: { width: MOBILE_DESIGN_WIDTH, height: MOBILE_VIEWPORT_HEIGHT },
    }) +
    '</body></html>'
  );
};

export const buildPreviewDocument = ({ method, payload, baseUrl, parentOrigin }) =>
  `<!doctype html><html><head><meta charset="utf-8">${baseTag(baseUrl)}<style>html,body{margin:0;padding:0}</style></head>` +
  `<body><div id="${PREVIEW_CONTAINER_ID}"></div>` +
  `<script src="${PREVIEW_BUNDLE_URL}"></script>` +
  `<script>(${installPreviewSdkStub.toString()})(window);(${bootstrapSdkPreview.toString()})(${inlineJson({
    method,
    payload,
    parentOrigin,
    messageType: PREVIEW_MESSAGE_TYPE,
    containerId: PREVIEW_CONTAINER_ID,
  })});</script></body></html>`;
